import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { BookOpen, Download, ShieldCheck, Lock, Info, Mail, ArrowLeft, Check, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayPalMark = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path
      fill="currentColor"
      d="M7.4 21.5H4.6l.4-2.5h2.8l-.4 2.5zm11.3-14.4c.6.7.9 1.7.7 3-.6 3.3-3 4.6-6.1 4.6h-.7c-.4 0-.7.3-.8.6l-.5 3.4-.2 1c0 .3-.3.5-.6.5H8l1.6-10.5c.1-.4.4-.6.8-.6h4c1.8 0 3.2.4 4 1.3.2.2.3.4.3.7zm-3.5-4.6H10c-.4 0-.7.3-.8.6L6.5 18.4h3l.7-4.6c.1-.4.4-.6.8-.6h1.4c3.5 0 6.2-1.4 7-5.2.2-1.5-.1-2.7-1-3.5-1-.9-2.7-1-4.2-1z"
    />
  </svg>
);

export default function BuyEbook() {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/ebook/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setError('Could not load product info.'));
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const payNow = async () => {
    setError('');
    if (!emailValid) {
      setError('Please enter a valid email so we can send your ebook.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ebook/order/initiate`, {
        buyer_email: email,
        buyer_name: name,
      });
      if (data?.paypal_url) {
        window.location.href = data.paypal_url;
      } else {
        throw new Error('Could not start checkout');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start PayPal checkout';
      setError(msg);
      toast({ title: 'Checkout error', description: msg });
    } finally {
      setLoading(false);
    }
  };

  if (!info) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        {error || 'Loading...'}
      </div>
    );
  }

  const price = String(info.price_usd || '4.99');

  return (
    <div>
      <SEO
        title={`Buy ${info.title} — Ebook`}
        description={`Purchase the ebook of ${info.title} by ${info.author} for $${price} USD. Instant secure download after payment.`}
        path="/single-handed/buy"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0b2545] via-[#123a6b] to-[#0b2545] text-white">
        <div className="relative max-w-7xl mx-auto px-4 py-14 md:py-20">
          <Link to="/single-handed" className="inline-flex items-center gap-2 text-amber-300 text-sm hover:text-amber-100 transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> Back to Single Handed
          </Link>
          <div className="grid md:grid-cols-[320px_1fr] gap-10 md:gap-16 items-center">
            <img
              src={info.cover_url}
              alt={`${info.title} cover`}
              className="w-full max-w-[300px] rounded-md shadow-2xl mx-auto"
            />
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-300/30 text-amber-300 text-xs font-semibold uppercase tracking-widest mb-5">
                <BookOpen className="h-3.5 w-3.5" /> Digital Ebook (EPUB)
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                {info.title}
              </h1>
              <div className="mt-3 text-amber-300 text-lg font-semibold">
                by {info.author}
              </div>
              <p className="mt-5 text-slate-200 leading-relaxed max-w-xl">
                {info.description}
              </p>

              <div className="mt-8 flex items-baseline gap-3">
                <span className="font-serif text-5xl font-bold text-amber-400">
                  ${price}
                </span>
                <span className="text-slate-300 text-sm">USD · one-time payment · instant download</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout */}
      <section className="max-w-5xl mx-auto px-4 py-12 md:py-16 grid md:grid-cols-[1fr_380px] gap-10">
        {/* Left: book value prop */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">What you get</div>
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#0b2545] mb-4">A digital ebook you can read on any device.</h2>
          <div className="h-1 w-16 bg-amber-400 mb-6" />
          <ul className="space-y-3 text-slate-700">
            {[
              'Instant EPUB download after you return from PayPal',
              'Readable on Kindle, iPad, iPhone, Android, Mac, PC, and all major ebook apps',
              'A secure one-time download link emailed to you — valid for 48 hours and up to 5 downloads',
              'This is a digital ebook — no physical book will be shipped',
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 p-5 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-4">
            <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <strong>How it works:</strong> Click <em>Pay with PayPal</em>, complete the $4.99 payment on
              PayPal's secure site, and you'll be returned here with an instant download link.
              A copy of the link is also emailed to the address you enter.
            </div>
          </div>
        </div>

        {/* Right: checkout */}
        <aside className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 h-fit sticky top-24">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">Checkout</div>
          <div className="font-serif text-xl font-bold text-[#0b2545] mb-1">{info.title}</div>
          <div className="text-sm text-slate-500 mb-5">EPUB ebook · 1 copy</div>

          <div className="flex items-baseline justify-between py-3 border-y border-slate-100">
            <span className="text-sm text-slate-600">Total</span>
            <span className="font-serif text-2xl font-bold text-[#0b2545]">${price}</span>
          </div>

          <div className="mt-5 space-y-3">
            <div>
              <Label htmlFor="buyer-name" className="text-slate-700">Your name</Label>
              <Input
                id="buyer-name"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="buyer-email" className="text-slate-700">Email for delivery</Label>
              <Input
                id="buyer-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
              />
              <div className="text-[11px] text-slate-500 mt-1">We'll email your secure download link here.</div>
            </div>
          </div>

          <button
            onClick={payNow}
            disabled={!emailValid || loading}
            className="mt-5 w-full flex items-center justify-center gap-3 px-6 py-4 rounded-md bg-[#ffc439] hover:bg-[#f0b429] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-[#003087] font-bold text-base transition-all shadow-sm hover:shadow-md"
          >
            {loading ? (
              'Opening PayPal...'
            ) : (
              <>
                <PayPalMark />
                <span>Pay ${price} with PayPal</span>
                <ExternalLink className="h-4 w-4 opacity-60" />
              </>
            )}
          </button>

          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-slate-100 space-y-2 text-xs text-slate-500">
            <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-amber-500" /> Secured by PayPal · Buyer Protection</div>
            <div className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-amber-500" /> We never see your card details</div>
            <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-amber-500" /> Download link emailed instantly</div>
            <div className="flex items-center gap-2"><Download className="h-3.5 w-3.5 text-amber-500" /> EPUB file · valid 48h / up to 5 downloads</div>
          </div>
        </aside>
      </section>
    </div>
  );
}
